import { useParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Col, Container, Row } from 'react-bootstrap';
import { useReactTable, getCoreRowModel, getSortedRowModel, flexRender } from '@tanstack/react-table';
import type { ColumnDef } from '@tanstack/react-table';
import type { ISummary } from '../../interfaces/ISummary';
import type { ISummaryElement } from '../../interfaces/ISummaryElement';
import Common from '../shared/Common';
import { Gallery, Item, useGallery } from 'react-photoswipe-gallery';
import { SortUpIcon, SortDownIcon, GalleryIcon } from '../shared/icons/Svgs';
import { Cell, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import './style.css';

const Summary = () => {
  type ImageWithSize = { pictureBase64: string; width: number; height: number };

  const [summary, setSummary] = useState<ISummary>({
    name: '',
    elements: [],
  });
  const [selectedPicture, setSelectedPicture] = useState<ImageWithSize | undefined>();
  const [galleryOpenTrigger, setGalleryOpenTrigger] = useState<number>(0);
  const [summaryLoading, setSummaryLoading] = useState<boolean>(true);
  const [pictureLoading, setPictureLoading] = useState<boolean>(false);
  const [summaryNotFound, setSummaryNotFound] = useState<boolean>(false);
  const [loadedPictures, setLoadedPictures] = useState<Map<number, ImageWithSize>>(new Map<number, ImageWithSize>());
  const [showMore, setShowMore] = useState(false);
  const [viewMode, setViewMode] = useState<number>(0);

  const GalleryContent = ({
    selectedPicture,
    openTrigger,
  }: {
    selectedPicture: ImageWithSize | undefined;
    openTrigger: number;
  }) => {
    const { open } = useGallery();

    useEffect(() => {
      if (openTrigger > 0) {
        open(0);
        setGalleryOpenTrigger(0);
      }
    }, [openTrigger, open]);

    return (
      <Item original={selectedPicture?.pictureBase64} width={selectedPicture?.width} height={selectedPicture?.height}>
        {({ ref }) => <span ref={ref} />}
      </Item>
    );
  };

  const showPrices = (viewMode & 0b0000001) > 0;
  const showLocations = (viewMode & 0b0000010) > 0;
  const showCategories = (viewMode & 0b0000100) > 0;
  const showDates = (viewMode & 0b0001000) > 0;
  const showPictures = (viewMode & 0b0010000) > 0;
  const showStatistics = (viewMode & 0b0100000) > 0;
  const showVat = (viewMode & 0b1000000) > 0 && summary?.elements.filter((e) => (e.vat ?? 0) > 0).length > 0;

  const fetchIdRef = useRef(0);
  const params = useParams();

  const getCostCellValue = useCallback(
    (element: ISummaryElement) => {
      let price = 0;
      if (showVat) {
        price = Math.round(((element.vat ?? 0) + 1) * (element.cost ?? 0) * 100) / 100;
        if ((element.vat ?? 0) > 0) {
          return (
            <div className='summary-cell-price'>
              {Common.Utils.getCurrencyString(price)} <span className='summary-cell-vat' />
              <span className='badge summary-cell-badge-vat'>-{(element.vat ?? 0) * 100}% VAT</span>
            </div>
          );
        } else {
          return Common.Utils.getCurrencyString(price);
        }
      } else {
        return Common.Utils.getCurrencyString(element.cost ?? 0);
      }
    },
    [showVat]
  );

  const columns = useMemo<ColumnDef<ISummaryElement>[]>(() => {
    const columns: ColumnDef<ISummaryElement>[] = [];
    columns.push({
      accessorFn: (row) => row.description,
      id: 'name',
      header: () => <span style={{ userSelect: 'none' }}>Opis</span>,
    });
    if (showDates) {
      columns.push({
        accessorFn: (row) => (row.payTime ? new Date(row.payTime) : new Date()),
        id: 'payTime',
        header: () => <span style={{ userSelect: 'none' }}>Data</span>,
        cell: (row) => <span>{Common.Utils.getDatetimeWithoutZoneConverter(row.row.original.payTime ?? '')}</span>,
      });
    }
    if (showLocations) {
      columns.push({
        accessorFn: (row) => row.locationName ?? '',
        id: 'locationName',
        header: () => <span style={{ userSelect: 'none' }}>Lokalizacja</span>,
      });
    }
    if (showCategories) {
      columns.push({
        accessorFn: (row) => row.categoryName ?? '',
        id: 'categoryName',
        header: () => <span style={{ userSelect: 'none' }}>Kategoria</span>,
      });
    }
    if (showPrices) {
      columns.push({
        accessorFn: (row) => row.cost,
        id: 'cost',
        header: () => <span style={{ userSelect: 'none' }}>{`${showVat ? 'Kwota brutto' : 'Kwota'}`}</span>,
        cell: (row) => <div>{getCostCellValue(row.row.original)}</div>,
      });
    }
    if (showVat) {
      columns.push({
        accessorFn: (row) => row.vat,
        id: 'totalCost',
        header: () => <span style={{ userSelect: 'none' }}>Kwota</span>,
        cell: (row) => <div>{`${Common.Utils.getCurrencyString(row.row.original.cost ?? 0)}`}</div>,
      });
    }
    if (showPictures) {
      columns.push({
        accessorFn: (row) => row.categoryName ?? '',
        id: 'picture',
        header: () => <span style={{ userSelect: 'none' }} />,
        cell: (info) => (
          <div>
            {(info.row.original.billItemPictureId ?? 0) > 0 && (
              <div
                style={{ cursor: 'pointer' }}
                title='Pokaż zdjęcie'
                onClick={async () => {
                  const loadedPicture = loadedPictures.get(info.row.original.billItemPictureId!);
                  if (loadedPicture) {
                    setSelectedPicture(loadedPicture);
                    setGalleryOpenTrigger((prev) => prev + 1);
                  } else {
                    const fetchId = ++fetchIdRef.current;

                    const response = await Common.authorizedFetch(
                      'api/summaries/getSummaryElementPicture?summaryId=' +
                        params.id +
                        '&pictureId=' +
                        info.row.original.billItemPictureId
                    );
                    if (fetchId === fetchIdRef.current) {
                      const data = await response.json();
                      if (data.success && data.result.billPicture) {
                        const img = new Image();
                        img.onload = function () {
                          const picture: ImageWithSize = {
                            width: img.width,
                            height: img.height,
                            pictureBase64: img.src,
                          };
                          setSelectedPicture(picture);
                          setGalleryOpenTrigger((prev) => prev + 1);
                          setLoadedPictures(new Map(loadedPictures.set(info.row.original.billItemPictureId!, picture)));
                        };
                        img.src = data.result.billPicture;
                      }
                    }
                    setPictureLoading(false);
                  }
                }}
              >
                <GalleryIcon
                  style={{
                    color: 'var(--app-main-color)',
                    width: '28px',
                    height: '28px',
                  }}
                />
              </div>
            )}
          </div>
        ),
      });
    }
    return columns;
  }, [
    showPrices,
    showLocations,
    showDates,
    showCategories,
    showPictures,
    showVat,
    loadedPictures,
    params.id,
    getCostCellValue,
  ]);

  const table = useReactTable({
    data: summary?.elements ?? [],
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      sorting: [
        {
          id: 'payTime',
          desc: true,
        },
      ],
    },
  });
  const fetchUserData = useCallback(async (id: string) => {
    setSummaryLoading(true);
    const fetchId = ++fetchIdRef.current;

    const response = await Common.authorizedFetch('api/summaries/getSummary?id=' + id);
    if (fetchId === fetchIdRef.current) {
      const data = await response.json();
      if (data.success) {
        if (!data.result.summary) {
          setSummaryNotFound(true);
        } else {
          setViewMode(data.result.viewMode);
          setSummary(data.result.summary);
        }
      }
      setSummaryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUserData(params.id ?? '');
  }, [fetchUserData, params.id]);

  const getOverallStatisticsCardContent = () => {
    const jsMaxTimestamp = 8640000000000000;
    const lineChartData: { date: number; value: number }[] = [];

    const maxDate = new Date(
      Math.max(
        ...summary.elements.map((e) => (e.payTime ? new Date(e.payTime) : new Date(-jsMaxTimestamp))).map(Number)
      )
    );
    const minDate = new Date(
      Math.min(...summary.elements.map((e) => (e.payTime ? new Date(e.payTime) : new Date(jsMaxTimestamp))).map(Number))
    );

    const datediff = (first: Date, second: Date) =>
      Math.round((second.valueOf() - first.valueOf()) / (1000 * 60 * 60 * 24));

    const diff = datediff(minDate, maxDate);

    // Grupowanie elementów według daty
    const groupedByDate = summary.elements.reduce(
      (acc, element) => {
        const date = element.payTime ? new Date(element.payTime).toISOString().split('T')[0] : 'Brak daty';
        acc[date] = (acc[date] || 0) + (element.cost ?? 0);
        return acc;
      },
      {} as Record<string, number>
    );

    // Sortowanie dat i obliczanie sumy narastającej
    const sortedDates = Object.keys(groupedByDate).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    let cumulativeSum = 0;

    sortedDates.forEach((date) => {
      cumulativeSum += groupedByDate[date];
      lineChartData.push({ date: new Date(date).getTime(), value: Math.round(cumulativeSum * 100) / 100 });
    });

    return (
      <Container>
        {generateLineChart(lineChartData)}
        <table className='table table-without-border-bottom' style={{ marginBottom: '11px' }}>
          <tbody>
            {showPrices && (
              <tr>
                <td style={{ width: '220px' }}>Suma wydanych pieniędzy</td>
                <td>
                  <b>{Common.Utils.getCurrencyString(totalPrice)}</b>
                </td>
              </tr>
            )}
            {showVat && (
              <tr>
                <td style={{ width: '220px' }}>Zwrócony podatek VAT</td>
                <td>{Common.Utils.getCurrencyString(totalVatCost)}</td>
              </tr>
            )}
            <tr>
              <td style={{ width: '220px' }}>Liczba pozycji</td>
              <td>{summary.elements.length}</td>
            </tr>
            {showDates && (
              <>
                <tr>
                  <td>Czas trwania (dni)</td>
                  <td>{Number.isNaN(diff) ? 0 : diff}</td>
                </tr>
                <tr>
                  <td>Rozpoczęto</td>
                  <td>{Common.Utils.getDatetimeWithoutZoneConverter(minDate.toDateString())}</td>
                </tr>
                <tr>
                  <td>Ostatnie</td>
                  <td>{Common.Utils.getDatetimeWithoutZoneConverter(maxDate.toDateString())}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </Container>
    );
  };

  const generatePieChart = (pieChartData: any[]) => {
    const COLORS = ['rgba(20,86,56,0.75)', '#63993D', '#87BB62', '#AFDC8F', '#3D7317', '#204D00'];
    const CustomTooltip = ({ active, payload }: any) => {
      if (active && payload && payload.length) {
        let name = payload[0].name;
        if (name === '' || !name) {
          name = 'Brak';
        }
        return (
          <div className='summary-pie-chart-tooltip'>
            <p>
              <b>{`${name}`}</b>
              {`: ${Common.Utils.getCurrencyString(payload[0].value)}`}
            </p>
          </div>
        );
      }

      return null;
    };
    return (
      <ResponsiveContainer width='100%' height={250}>
        <PieChart>
          <Pie
            animationDuration={1000}
            dataKey='value'
            data={pieChartData}
            outerRadius={95}
            fill='var(--app-main-color)'
            cx='50%'
            cy='50%'
          >
            {pieChartData.map((_, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
        </PieChart>
      </ResponsiveContainer>
    );
  };

  const generateLineChart = (lineChartData: any[]) => {
    const CustomTooltip = ({ active, payload }: any) => {
      if (active && payload && payload.length) {
        const dataPoint = payload[0];
        return (
          <div className='summary-line-chart-tooltip'>
            <p>
              <b>{`${new Date(dataPoint.payload.date).toISOString().split('T')[0]}`}</b>
              {`: ${Common.Utils.getCurrencyString(dataPoint.value)}`}
            </p>
          </div>
        );
      }
      return null;
    };

    return (
      <ResponsiveContainer width='100%' height={250}>
        <LineChart
          accessibilityLayer={false}
          margin={{
            right: 20,
            top: 25,
            bottom: 5,
          }}
          data={lineChartData}
        >
          <XAxis
            type='number'
            domain={['dataMin', 'dataMax']}
            dataKey='date'
            stroke='rgba(20,86,56,0.75)'
            hide={true}
          />
          <YAxis
            width={'auto'}
            stroke='rgba(20,86,56,0.75)'
            domain={[0, 'dataMax']}
            tickFormatter={(value) => {
              if (value >= 1000000) {
                return `${(value / 1000000).toFixed(1)}M`;
              } else if (value >= 100000) {
                return `${(value / 1000).toFixed(1)}k`;
              }
              return Math.round(value.toString()).toString();
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Line
            type='monotone'
            dataKey='value'
            stroke='rgba(20,86,56,0.75)'
            strokeWidth={2}
            dot={{ r: 4 }}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  const getLocationsStatisticsCardContent = () => {
    const getTotalPriceForSummaryElements = (elements: ISummaryElement[]) =>
      elements.reduce((previousValue, currentValue) => previousValue + (currentValue.cost ?? 0), 0);
    const locationsRows = [];
    const pieChartData = [];
    const locationsGroups = Common.Utils.groupBy(summary.elements, (e) => e.locationName ?? '');
    const sortableArray = Object.entries(locationsGroups);
    const sortedArray = sortableArray.sort(
      ([, a], [, b]) => getTotalPriceForSummaryElements(b) - getTotalPriceForSummaryElements(a)
    );
    const sortedLocationsGroups = Object.fromEntries(sortedArray);
    const sortedLocationsKeys = Object.keys(sortedLocationsGroups);
    for (let ind = 0; ind < sortedLocationsKeys.length; ind++) {
      const location = sortedLocationsKeys[ind];
      const elements = sortedLocationsGroups[location];
      const totalPrice = getTotalPriceForSummaryElements(elements);
      locationsRows.push(
        <tr key={ind}>
          <td>{location}</td>
          <td>{Common.Utils.getCurrencyString(totalPrice)}</td>
        </tr>
      );
      pieChartData.push({ name: location, value: totalPrice });
    }
    return (
      <Container>
        {generatePieChart(pieChartData)}
        <table className='table table-without-border-bottom' style={{ marginBottom: '11px' }}>
          <tbody>{locationsRows}</tbody>
        </table>
      </Container>
    );
  };

  const getCategoriesStatisticsCardContent = () => {
    const getTotalPriceForSummaryElements = (elements: ISummaryElement[]) =>
      elements.reduce((previousValue, currentValue) => previousValue + (currentValue.cost ?? 0), 0);
    const categoriesRows = [];
    const pieChartData = [];
    const categoriesGroups = Common.Utils.groupBy(summary.elements, (e) => e.categoryName ?? '');
    const sortableArray = Object.entries(categoriesGroups);
    const sortedArray = sortableArray.sort(
      ([, a], [, b]) => getTotalPriceForSummaryElements(b) - getTotalPriceForSummaryElements(a)
    );
    const sortedCategoriesGroups = Object.fromEntries(sortedArray);
    const sortedCategoriesKeys = Object.keys(sortedCategoriesGroups);
    for (let ind = 0; ind < sortedCategoriesKeys.length; ind++) {
      const location = sortedCategoriesKeys[ind];
      const elements = sortedCategoriesGroups[location];
      const totalPrice = getTotalPriceForSummaryElements(elements);
      categoriesRows.push(
        <tr key={ind}>
          <td>{location}</td>
          <td>{Common.Utils.getCurrencyString(totalPrice)}</td>
        </tr>
      );
      pieChartData.push({ name: location, value: totalPrice });
    }
    return (
      <Container>
        {generatePieChart(pieChartData)}
        <table className='table table-without-border-bottom' style={{ marginBottom: '11px' }}>
          <tbody>{categoriesRows}</tbody>
        </table>
      </Container>
    );
  };

  const totalPrice = summary.elements
    .map((e) => e.cost ?? 0)
    .reduce((previousValue, currentValue) => previousValue + currentValue, 0);

  const totalVatCost = summary.elements
    .filter((e) => (e.vat ?? 0) > 0)
    .map((e) => (e.cost ?? 0) * (e.vat ?? 0))
    .reduce((previousValue, currentValue) => previousValue + currentValue, 0);

  const maxElementsInNotCollpasedTable = 10;

  return (
    <>
      <div>
        {selectedPicture && (
          <Gallery>
            <GalleryContent selectedPicture={selectedPicture} openTrigger={galleryOpenTrigger} />
          </Gallery>
        )}
      </div>
      <Container className='mt-2'>
        {summaryNotFound ? (
          <h1 className='text-danger'>Podsumowanie niedostępne</h1>
        ) : (
          <>
            {Common.Ui.showLoadingSpinnerFixed(summaryLoading || pictureLoading)}
            {summaryLoading && (
              <center>
                <h3 className='mb-3'>Ładowanie podsumowania</h3>
              </center>
            )}
            <Container>
              <Row className='mt-2'>
                {!summaryLoading && summary.elements.length === 0 ? (
                  <center>
                    <h3 className='mb-3'>Brak elementów w podsumowaniu</h3>
                  </center>
                ) : (
                  <div>
                    <Card className='my-2 p-0'>
                      <Card.Header className='summary-statistics-card-header'>
                        Podsumowanie <b>{summary.name}</b>
                      </Card.Header>
                      <Card.Body className='py-0'>
                        <div className='summary-table table-responsive-sm'>
                          <table
                            id='summary-elements-table'
                            className='table table-without-border-bottom'
                            style={{ marginBottom: '11px' }}
                          >
                            <thead>
                              {table.getHeaderGroups().map((headerGroup) => (
                                <tr key={headerGroup.id}>
                                  {headerGroup.headers.map((header) => (
                                    <th key={header.id}>
                                      {header.isPlaceholder ? null : (
                                        <div
                                          {...{
                                            style: header.column.getCanSort()
                                              ? {
                                                  cursor: 'pointer',
                                                }
                                              : {},
                                            onClick: header.column.getToggleSortingHandler(),
                                          }}
                                        >
                                          {flexRender(header.column.columnDef.header, header.getContext())}
                                          {{
                                            asc: <SortUpIcon style={{ marginLeft: '5px' }} />,
                                            desc: <SortDownIcon style={{ marginLeft: '5px' }} />,
                                          }[header.column.getIsSorted() as string] ?? null}
                                        </div>
                                      )}
                                    </th>
                                  ))}
                                </tr>
                              ))}
                            </thead>
                            <tbody>
                              {summaryLoading ? (
                                Common.Ui.getSkeletonTableRows(6, 6, 35)
                              ) : (
                                <>
                                  {table
                                    .getRowModel()
                                    .rows.slice(0, showMore ? 100000 : maxElementsInNotCollpasedTable)
                                    .map((row) => (
                                      <tr key={row.id}>
                                        {row.getVisibleCells().map((cell) => (
                                          <td key={cell.id}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                          </td>
                                        ))}
                                      </tr>
                                    ))}
                                </>
                              )}
                            </tbody>
                          </table>
                        </div>
                        {summary.elements.length > maxElementsInNotCollpasedTable && (
                          <div className='text-center py-2'>
                            <Button onClick={() => setShowMore((prev) => !prev)}>
                              {showMore ? 'Pokaż mniej...' : 'Pokaż więcej...'}
                            </Button>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                    {showStatistics && (
                      <Row>
                        <Col xl={4} md={6}>
                          <Card className='my-2'>
                            <Card.Header className='summary-statistics-card-header'>Statystyki</Card.Header>
                            {getOverallStatisticsCardContent()}
                          </Card>
                        </Col>
                        {showLocations && (
                          <Col xl={4} md={6}>
                            <Card className='my-2'>
                              <Card.Header className='summary-statistics-card-header'>Lokalizacje</Card.Header>
                              {getLocationsStatisticsCardContent()}
                            </Card>
                          </Col>
                        )}
                        {showCategories && (
                          <Col xl={4} md={6}>
                            <Card className='my-2'>
                              <Card.Header className='summary-statistics-card-header'>Kategorie</Card.Header>
                              {getCategoriesStatisticsCardContent()}
                            </Card>
                          </Col>
                        )}
                      </Row>
                    )}
                  </div>
                )}
              </Row>
            </Container>
          </>
        )}
      </Container>
    </>
  );
};

export default Summary;
